<template>
  <v-dialog
    v-model="dialog"
    max-width="800"
    scrollable
  >
    <v-card
      v-if="category"
      class="category-details-modal"
    >
      <!-- Header com botão fechar -->
      <v-btn
        icon
        class="close-btn"
        aria-label="Fechar detalhes do quarto"
        @click="close"
      >
        <v-icon>{{ icons.mdiClose }}</v-icon>
      </v-btn>

      <!-- Carrossel de Imagens -->
      <div
        v-if="hasImages"
        class="image-carousel"
      >
        <v-carousel
          v-model="currentSlide"
          height="300"
          hide-delimiter-background
          show-arrows-on-hover
          cycle
          :interval="5000"
        >
          <v-carousel-item
            v-for="image in category.images"
            :key="image.id"
          >
            <v-img
              :src="image.url"
              height="300"
              class="grey lighten-2"
            >
              <template v-slot:placeholder>
                <v-row
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </v-carousel-item>
        </v-carousel>
      </div>

      <!-- Placeholder quando não há imagens -->
      <div
        v-else
        class="no-image-placeholder"
      >
        <v-icon
          size="64"
          color="grey lighten-1"
        >
          {{ icons.mdiImageOff }}
        </v-icon>
        <p class="grey--text mt-2">
          Sem imagens disponíveis
        </p>
      </div>

      <v-card-text class="pt-4">
        <!-- Título e Info básica -->
        <div class="details-header">
          <div class="details-header__info">
            <h2 class="details-title">
              {{ category.display_name || category.name }}
            </h2>
            <div
              v-if="category.occupancy_name"
              class="text-body-2 text--secondary mb-2"
            >
              Composição: {{ category.occupancy_name }}
            </div>
            <div class="details-chips">
              <v-chip
                v-if="category.size"
                small
                outlined
                class="mr-2 mb-1"
              >
                <v-icon
                  small
                  left
                >
                  {{ icons.mdiRulerSquare }}
                </v-icon>
                {{ category.size }}
              </v-chip>
              <v-chip
                v-if="category.bed_type"
                small
                outlined
                class="mr-2 mb-1"
              >
                <v-icon
                  small
                  left
                >
                  {{ icons.mdiBed }}
                </v-icon>
                {{ category.bed_type }}
              </v-chip>
              <v-chip
                v-if="category.max_guests"
                small
                outlined
                class="mr-2 mb-1"
              >
                <v-icon
                  small
                  left
                >
                  {{ icons.mdiAccountGroup }}
                </v-icon>
                Até {{ category.max_guests }} {{ category.max_guests === 1 ? 'hóspede' : 'hóspedes' }}
              </v-chip>
            </div>
          </div>
          <div class="details-price">
            <div class="details-price__label">
              a partir de
            </div>
            <div class="details-price__value">
              {{ formatCurrency(category.base_price) }}
            </div>
            <div class="details-price__sub">
              por diária
            </div>
          </div>
        </div>

        <!-- Descrição -->
        <div
          v-if="category.detailed_description"
          class="details-description"
        >
          <p class="details-description__text">
            {{ category.detailed_description }}
          </p>
        </div>

        <!-- Comodidades agrupadas -->
        <div
          v-if="hasAmenities"
          class="amenities-section"
        >
          <v-divider class="mb-4"></v-divider>

          <div
            v-for="group in category.grouped_amenities"
            :key="group.group"
            class="amenity-group"
          >
            <div class="amenity-group__title">
              <v-icon
                small
                color="success"
                class="mr-2"
              >
                {{ icons.mdiCheckCircle }}
              </v-icon>
              <span class="font-weight-medium">{{ group.group_name }}</span>
            </div>
            <div class="amenities-list">
              <span
                v-for="(amenity, index) in group.items"
                :key="amenity.id"
                class="amenity-item"
              >
                <v-icon
                  v-if="resolveMdiIcon(amenity.icon)"
                  x-small
                  class="mr-1"
                >
                  {{ resolveMdiIcon(amenity.icon) }}
                </v-icon>
                {{ amenity.name }}{{ amenitySeparator(index, group.items) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Resumo da reserva -->
        <v-divider class="my-4"></v-divider>

        <v-card
          outlined
          class="booking-summary pa-4"
        >
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="text-body-2">{{ category.days }} {{ category.days === 1 ? 'diária' : 'diárias' }}</span>
            <span class="font-weight-bold">{{ formatCurrency(category.total_price) }}</span>
          </div>

          <PriceBreakdown
            :price-breakdown="category.price_breakdown"
            :child-breakdown="category.child_breakdown"
            :daily="category.daily"
            include-daily
          />

          <v-divider class="my-2"></v-divider>

          <div class="d-flex justify-space-between align-center">
            <span class="text-subtitle-1 font-weight-bold">Total</span>
            <span class="text-h6 font-weight-bold primary--text">
              {{ formatCurrency(category.total_price) }}
            </span>
          </div>

          <div
            v-if="category.require_payment"
            class="mt-2 text-body-2"
          >
            <v-icon
              small
              color="info"
              class="mr-1"
            >
              {{ icons.mdiInformation }}
            </v-icon>
            <span class="grey--text">
              {{
                category.no_show_guarantee
                  ? 'Garantia no-show: ' + formatCurrency(category.advance_payment)
                  : 'Pagamento antecipado: ' + formatCurrency(category.advance_payment)
              }}
            </span>
          </div>
        </v-card>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-btn
          class="back-btn"
          color="secondary"
          dark
          @click="close"
        >
          Voltar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          large
          @click="selectRoom"
        >
          <v-icon left>
            {{ icons.mdiBed }}
          </v-icon>
          Reservar Agora
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as mdiIcons from '@mdi/js'
import {
  mdiAccountGroup,
  mdiBed,
  mdiCheckCircle,
  mdiClose,
  mdiImageOff,
  mdiInformation,
  mdiRulerSquare,
} from '@mdi/js'
import PriceBreakdown from './PriceBreakdown.vue'

export default {
  name: 'CategoryDetailsModal',
  components: { PriceBreakdown },

  props: {
    value: Boolean,
    category: { type: Object, default: null },
  },

  data: () => ({
    currentSlide: 0,
    icons: {
      mdiAccountGroup,
      mdiBed,
      mdiCheckCircle,
      mdiClose,
      mdiImageOff,
      mdiInformation,
      mdiRulerSquare,
    },
  }),

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    hasImages() {
      return this.category?.images?.length > 0
    },

    hasAmenities() {
      return this.category?.grouped_amenities?.length > 0
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.currentSlide = 0
      }
    },
  },

  methods: {
    close() {
      this.dialog = false
    },

    selectRoom() {
      this.$emit('select', this.category)
      this.close()
    },
    resolveMdiIcon(name) {
      if (!name) return null
      if (name.startsWith('mdi')) {
        if (!name.includes('-')) return mdiIcons[name] || null
        const key = `mdi${name
          .replace(/^mdi-/, '')
          .split('-')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join('')}`

        return mdiIcons[key] || null
      }

      return null
    },

    amenitySeparator(index, items) {
      return index === items.length - 1 ? '' : ','
    },

    formatCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return 'R$ 0,00'
      }
      const number = Number(value)

      return number.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
  },
}
</script>

<style scoped>
.category-details-modal {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.92) !important;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.image-carousel :deep(.v-carousel) {
  border-radius: 0 0 18px 18px;
}

.no-image-placeholder {
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.details-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.details-title {
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.details-chips .v-chip {
  border-color: rgba(0, 0, 0, 0.12);
}

.details-price {
  text-align: right;
  background: #f7f9ff;
  border-radius: 12px;
  padding: 10px 14px;
  min-width: 140px;
}

.details-price__label,
.details-price__sub {
  font-size: 0.75rem;
  color: #6b7280;
}

.details-price__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e40af;
}

.details-description {
  margin-bottom: 16px;
}

.details-description__text {
  white-space: pre-line;
  color: #4b5563;
  line-height: 1.6;
}

.amenities-section {
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
}

.amenity-group {
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.amenity-group:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.amenity-group__title {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.amenities-list {
  color: #666;
  font-size: 0.9rem;
  padding-left: 12px;
}

.amenity-item {
  display: inline;
  margin-right: 4px;
}

.booking-summary {
  background: linear-gradient(135deg, #f8f9fa 0%, #e8f4f8 100%);
  border-color: #e0e0e0 !important;
}

.back-btn :deep(.v-btn__content) {
  justify-content: center;
  width: 100%;
}

@media (max-width: 600px) {
  .details-header {
    flex-direction: column;
  }

  .details-price {
    width: 100%;
    text-align: left;
  }
}
</style>
